import { useRef, useState } from 'react';
import { sqlImportDraftEquals, type SqlImportDraft } from './draft';

/**
 * A draft under edit and the baseline it is compared against (as loaded, then as last saved).
 * Both are readable synchronously too: a leave guard asks whether there are unsaved changes in
 * the same tick a save re-baselines the draft and navigates, before React has re-rendered.
 */
export function useBaselinedDraft(initialDraft: SqlImportDraft) {
  const [draft, setDraft] = useState<SqlImportDraft>(initialDraft);
  const [baseline, setBaseline] = useState<SqlImportDraft>(initialDraft);
  const draftRef = useRef(draft);
  const baselineRef = useRef(baseline);
  draftRef.current = draft;
  baselineRef.current = baseline;

  const setDraftFields = (changes: Partial<SqlImportDraft>) => {
    draftRef.current = { ...draftRef.current, ...changes };
    setDraft(draftRef.current);
  };

  /** Drop the edits; the draft is the baseline again. */
  const resetToBaseline = () => {
    draftRef.current = baselineRef.current;
    setDraft(baselineRef.current);
  };

  /** The save that landed carried `sent`; edits typed while it was in flight stay unsaved. */
  const rebaseline = (sent: SqlImportDraft) => {
    baselineRef.current = sent;
    setBaseline(sent);
  };

  return {
    draft,
    baseline,
    /** The draft as of now, not as of the last render. */
    currentDraft: () => draftRef.current,
    /** Whether the draft differs from the baseline, read live. */
    differsFromBaseline: () => !sqlImportDraftEquals(draftRef.current, baselineRef.current),
    setDraftFields,
    resetToBaseline,
    rebaseline,
  };
}
